import { EVENTS, GAME_STORAGE_KEYS } from '../config'
import { Daily5State } from '../types'
import fetchDailyPuzzle from '../util/fetchDailyPuzzle'

const STORAGE_KEY = (isArchive: boolean) =>
	isArchive ? GAME_STORAGE_KEYS.DAILY5_ARCHIVE_STATE : GAME_STORAGE_KEYS.DAILY5_STATE

export async function loadDaily5State(puzzleDate: string, isArchive = false): Promise<Daily5State> {
	const serializedState = localStorage.getItem(STORAGE_KEY(isArchive))

	if (serializedState) {
		const deserializedState: Daily5State = JSON.parse(serializedState)
		// Does the state we’re loading from localStorage pertain to the
		// current puzzle being shown to the user?
		if (deserializedState.puzzleId === puzzleDate) {
			console.debug('Loaded current puzzle state from storage')
			return deserializedState
		}
	}

	// Either the state in storage doesn't belong to the current puzzle
	// being shown to the user (stale), or there's no state in storage.
	// Load the puzzle from the server, and initialse the default daily state.
	console.log('No state found for current puzzle in storage, starting fresh')
	const puzzle = await fetchDailyPuzzle(puzzleDate)
	const freshState: Daily5State = {
		puzzleId: puzzle.id,
		board: puzzle.board,
		time: 0,
		wordsRemaining: puzzle.words,
	}
	saveDaily5State(freshState, isArchive)
	return freshState
}

export function saveDaily5State(daily5State: Daily5State, isArchive = false) {
	const serializedState = JSON.stringify(daily5State)
	localStorage.setItem(STORAGE_KEY(isArchive), serializedState)
	emitStateChangeEvent(isArchive)
}

export function clearDaily5State(isArchive = false) {
	localStorage.removeItem(STORAGE_KEY(isArchive))
	emitStateChangeEvent(isArchive)
}

/**
 * Gets whatever the current value is for the daily5_state property in storage,
 * which might be empty. Be sure that you don't want `loadDaily5State` instead.
 */
export function readDaily5State(): Daily5State | null {
	const serializedState = localStorage.getItem(STORAGE_KEY(false))
	if (serializedState) {
		return JSON.parse(serializedState)
	}
	return null
}

export function emitStateChangeEvent(isArchive = false) {
	if (isArchive) {
		document.dispatchEvent(new CustomEvent(EVENTS.DAILY5_ARCHIVE_STATE_CHANGE))
	} else {
		document.dispatchEvent(new CustomEvent(EVENTS.DAILY5_STATE_CHANGE))
	}
}
